import { useProfileDraft, type WorkspaceTab } from "@/context/ProfileDraftContext";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/Tooltip";
import { AccountMenu } from "./AccountMenu";
import {
  Rocket,
  Loader2,
  AlertCircle,
  Eye,
  EyeOff,
  ChevronRight,
  ExternalLink,
  Check,
  Save,
} from "lucide-react";

interface WorkspaceToolbarProps {
  onTogglePreview?: () => void;
  className?: string;
}

export function WorkspaceToolbar({
  onTogglePreview,
  className = "",
}: WorkspaceToolbarProps) {
  const {
    activeTab,
    autosaveStatus,
    handleSaveDraft,
    isSavingDraft,
    handlePublish,
    isPublishing,
    previewOpen,
    setPreviewOpen,
    hasUnsavedChanges = false,
    hasUnpublishedChanges = false,
    isPublished = false,
    publishedUrl,
    setIsConflictModalOpen,
  } = useProfileDraft();

  const workspaceLabels: Record<WorkspaceTab, string> = {
    home: "Overview",
    content: "Content Studio",
    design: "Appearance Studio",
    pages: "Multi-Page",
    publish: "Publish & SEO",
  };

  const renderAutosaveStatus = () => {
    if (autosaveStatus === "saving") {
      return (
        <span
          className="flex items-center gap-1.5 text-xs text-muted-foreground px-2 py-1 select-none"
          title="Saving draft changes automatically..."
        >
          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
          <span className="hidden sm:inline">Saving…</span>
        </span>
      );
    }
    if (autosaveStatus === "saved") {
      return (
        <span
          className="flex items-center gap-1.5 text-xs text-muted-foreground px-2 py-1 select-none"
          title="All changes saved to cloud draft"
        >
          <Check className="h-3 w-3 text-emerald-500" />
          <span className="hidden sm:inline font-medium">Saved just now</span>
        </span>
      );
    }
    if (autosaveStatus === "conflict" || autosaveStatus === "error") {
      return (
        <button
          type="button"
          onClick={() => {
            if (autosaveStatus === "conflict") {
              setIsConflictModalOpen(true);
            } else {
              handleSaveDraft();
            }
          }}
          className="flex items-center gap-1.5 text-xs text-destructive font-medium px-2 py-1 rounded-md bg-destructive/10 border border-destructive/20 hover:bg-destructive/20 transition-colors cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-destructive"
        >
          <AlertCircle className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">
            {autosaveStatus === "conflict" ? "Resolve Conflict" : "Save failed — Retry"}
          </span>
        </button>
      );
    }
    if (hasUnsavedChanges) {
      return (
        <span
          className="flex items-center gap-1.5 text-xs text-muted-foreground px-2 py-1 select-none"
          title="Local edits waiting to save"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-amber-500/80" />
          <span className="hidden sm:inline">Unsaved changes</span>
        </span>
      );
    }
    return null;
  };

  return (
    <header
      role="banner"
      className={`sticky top-0 z-40 flex h-14 w-full items-center justify-between border-b border-border bg-background/95 backdrop-blur-md px-3 sm:px-5 shrink-0 select-none ${className}`}
    >
      {/* ── Left: Breadcrumbs & Current Workspace Title ── */}
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-sm font-bold tracking-tight text-foreground flex items-center shrink-0">
          Profolio<span className="text-primary">.lk</span>
        </span>
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
        <span className="text-sm font-semibold text-foreground truncate max-w-[180px] sm:max-w-none">
          {workspaceLabels[activeTab] || "Editor"}
        </span>
      </div>

      {/* ── Right: Autosave Indicator, Preview Toggle, Publish CTA, Account Menu ── */}
      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        {/* Autosave Status */}
        <div>{renderAutosaveStatus()}</div>

        {/* Live Preview Toggle Button */}
        <Tooltip
          content={
            previewOpen
              ? "Hide Live Preview (Cmd + `)"
              : "Show Live Preview (Cmd + `)"
          }
          side="bottom"
        >
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              if (onTogglePreview) {
                onTogglePreview();
              } else {
                setPreviewOpen(!previewOpen);
              }
            }}
            aria-label={previewOpen ? "Hide live preview" : "Show live preview"}
            aria-pressed={previewOpen}
            className={`h-9 px-2.5 sm:px-3 text-xs font-medium gap-1.5 cursor-pointer transition-colors ${
              previewOpen
                ? "bg-primary/10 border-primary/40 text-primary hover:bg-primary/20"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {previewOpen ? (
              <EyeOff className="h-4 w-4 shrink-0" />
            ) : (
              <Eye className="h-4 w-4 shrink-0" />
            )}
            <span className="hidden md:inline">Preview</span>
          </Button>
        </Tooltip>

        {/* View Live Site Button */}
        <Tooltip
          content={
            isPublished
              ? `Open live site in new tab (${publishedUrl || ""})`
              : "Publish site to view live"
          }
          side="bottom"
        >
          <span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!isPublished}
              onClick={() => {
                if (publishedUrl) {
                  window.open(publishedUrl, "_blank", "noopener,noreferrer");
                }
              }}
              aria-label="View live published website in new tab"
              className="h-9 px-2.5 sm:px-3 text-xs font-medium gap-1.5 cursor-pointer text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              <span className="hidden sm:inline">View Live</span>
            </Button>
          </span>
        </Tooltip>

        {/* Dedicated Save Draft Action Button */}
        <Tooltip
          content={
            isSavingDraft
              ? "Saving draft changes..."
              : autosaveStatus === "conflict"
              ? "Resolve version conflicts before saving"
              : hasUnsavedChanges
              ? "Save all pending changes to draft without publishing (Cmd + S)"
              : "Draft is up to date"
          }
          side="bottom"
        >
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isSavingDraft || isPublishing || autosaveStatus === "conflict" || !hasUnsavedChanges}
            onClick={() => handleSaveDraft()}
            aria-label="Save Draft"
            className={`h-9 px-2.5 sm:px-3 text-xs font-medium gap-1.5 cursor-pointer transition-colors ${
              hasUnsavedChanges
                ? "border-primary/50 text-primary hover:bg-primary/10"
                : "text-muted-foreground opacity-60 cursor-default"
            }`}
          >
            {isSavingDraft ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
            ) : (
              <Save className="h-3.5 w-3.5 shrink-0" />
            )}
            <span className="hidden sm:inline">
              {isSavingDraft ? "Saving..." : "Save Draft"}
            </span>
          </Button>
        </Tooltip>

        {/* Publish Action Button (4-State Visual Model) */}
        <Tooltip
          content={
            isPublishing
              ? "Building and publishing site snapshot..."
              : autosaveStatus === "conflict"
              ? "Resolve version conflicts before publishing"
              : autosaveStatus === "error"
              ? "Resolve save errors before publishing"
              : hasUnsavedChanges
              ? "Save pending changes and publish live"
              : hasUnpublishedChanges
              ? "Publish saved draft changes to live site"
              : "All changes published live (Click to re-publish)"
          }
          side="bottom"
        >
          <Button
            type="button"
            size="sm"
            variant={isPublishing || hasUnpublishedChanges ? "default" : "outline"}
            disabled={isPublishing || isSavingDraft || autosaveStatus === "conflict" || autosaveStatus === "error"}
            onClick={() => handlePublish()}
            aria-label="Publish site changes live"
            className={`h-9 px-3 sm:px-4 text-xs font-semibold gap-1.5 shadow-xs cursor-pointer transition-all ${
              isPublishing
                ? "opacity-80 cursor-wait"
                : hasUnsavedChanges
                ? "bg-amber-600 hover:bg-amber-500 text-white dark:bg-amber-600 dark:hover:bg-amber-500"
                : hasUnpublishedChanges
                ? "bg-primary hover:bg-primary/90 text-primary-foreground"
                : "border-border text-muted-foreground hover:text-foreground hover:bg-muted/50"
            }`}
          >
            {isPublishing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
            ) : !hasUnpublishedChanges ? (
              <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
            ) : (
              <Rocket className="h-3.5 w-3.5 shrink-0" />
            )}
            <span>
              {isPublishing
                ? "Publishing..."
                : hasUnsavedChanges
                ? "Save & Publish"
                : hasUnpublishedChanges
                ? "Publish Live"
                : "Published"}
            </span>
          </Button>
        </Tooltip>

        {/* Account Menu */}
        <AccountMenu align="right" />
      </div>
    </header>
  );
}
